import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { InactiveAssignee, InactiveResolution } from '../utils/taskInactiveAssignees';

interface InactiveAssigneesDialogProps {
  inactive: InactiveAssignee[];
  onResolve: (choice: InactiveResolution) => void;
}

const kindLabel = (kind: InactiveAssignee['kind']) => {
  if (kind === 'employee') return 'Employee';
  if (kind === 'position') return 'Position';
  return 'Schedule';
};

const InactiveAssigneesDialog: React.FC<InactiveAssigneesDialogProps> = ({ inactive, onResolve }) => {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onClick={() => onResolve('cancel')}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Inactive assignees</h3>
            <p className="text-sm text-neutral-600 mt-1">
              The following assignees are inactive and cannot be saved as-is:
            </p>
          </div>
        </div>
        <ul className="mb-4 space-y-2 max-h-48 overflow-y-auto">
          {inactive.map(item => (
            <li
              key={`${item.kind}-${item.id}`}
              className="text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded px-3 py-2"
            >
              <span className="font-medium">{kindLabel(item.kind)}:</span> {item.name}
            </li>
          ))}
        </ul>
        <p className="text-sm text-neutral-600 mb-5">
          Remove inactive assignees to update the form, ignore to save without them, or cancel.
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={() => onResolve('cancel')}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onResolve('ignore')}
            className="px-4 py-2 text-sm font-medium text-neutral-800 bg-neutral-100 border border-neutral-300 rounded-md hover:bg-neutral-200"
          >
            Ignore
          </button>
          <button
            type="button"
            onClick={() => onResolve('remove')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Remove inactive
          </button>
        </div>
      </div>
    </div>
  );
};

export default InactiveAssigneesDialog;
